import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Select } from '../components/Select';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface Manuscript {
  id: string;
  status: string;
  title: string;
  primaryGenre: string;
  subGenre: string;
  wordCount: number;
  betaReaderCount: number;
  applicationCount: number;
  lastUpdated: string;
  synopsis: string;
  firstPages: string;
  compTitles: string[];
  contentWarnings: string;
  seekingBetaReaders: boolean;
  createdAt: string;
}

export const ManuscriptDetailPage = () => {
  // Extract manuscript ID from URL
  const manuscriptId = window.location.pathname.split('/')[3];

  const [manuscript, setManuscript] = useState<Manuscript | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchManuscript();
  }, [manuscriptId]);

  const fetchManuscript = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.manuscripts.detail(manuscriptId), {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch manuscript');
        setLoading(false);
        return;
      }

      setManuscript(data.manuscript);
      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscript error:', err);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'drafting', label: 'Drafting' },
    { value: 'seeking', label: 'Seeking Feedback' },
    { value: 'querying', label: 'Querying' },
    { value: 'on-submission', label: 'On Submission' },
    { value: 'archived', label: 'Archived' },
  ];

  const handleStatusChange = async (newStatus: string) => {
    setIsUpdating(true);

    try {
      // Map frontend status to backend enum
      const statusMap: Record<string, string> = {
        'drafting': 'DRAFT',
        'seeking': 'SEEKING_FEEDBACK',
        'querying': 'QUERYING',
        'on-submission': 'ON_SUBMISSION',
        'archived': 'COMPLETE',
      };

      const backendStatus = statusMap[newStatus] || 'DRAFT';

      const response = await fetch(API_ENDPOINTS.manuscripts.update(manuscriptId), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: backendStatus }),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.error || 'Failed to update status');
        setIsUpdating(false);
        return;
      }

      if (manuscript) {
        setManuscript({ ...manuscript, status: newStatus });
      }
      setIsUpdating(false);
      window.alert('Status updated successfully!');
    } catch (err) {
      console.error('Update status error:', err);
      window.alert('Network error. Please try again.');
      setIsUpdating(false);
    }
  };

  const toggleBetaReaders = async () => {
    if (!manuscript) return;
    setIsUpdating(true);
    const newValue = !manuscript.seekingBetaReaders;

    try {
      const response = await fetch(API_ENDPOINTS.manuscripts.update(manuscriptId), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ seekingBetaReaders: newValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.error || 'Failed to update beta reader settings');
        setIsUpdating(false);
        return;
      }

      setManuscript({ ...manuscript, seekingBetaReaders: newValue });
      setIsUpdating(false);
      window.alert(newValue ? 'Now accepting beta reader applications' : 'Beta reader applications closed');
    } catch (err) {
      console.error('Toggle beta readers error:', err);
      window.alert('Network error. Please try again.');
      setIsUpdating(false);
    }
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/manuscripts/${manuscriptId}`;
    // In a real app, would copy to clipboard
    window.alert(`Share URL copied to clipboard:\n${shareUrl}`);
  };

  const handleExport = () => {
    console.log('Exporting manuscript data');
    window.alert('Manuscript data exported as PDF');
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'drafting' | 'seeking' | 'querying' | 'success' | 'default'; label: string }> = {
      drafting: { variant: 'drafting', label: 'Drafting' },
      seeking: { variant: 'seeking', label: 'Seeking Feedback' },
      querying: { variant: 'querying', label: 'Querying' },
      'on-submission': { variant: 'success', label: 'On Submission' },
      archived: { variant: 'default', label: 'Archived' },
    };
    const config = statusMap[status] || statusMap.drafting;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Helper function to map backend status to frontend format
  const mapStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      DRAFT: 'drafting',
      SEEKING_BETA: 'seeking',
      QUERYING: 'querying',
      PUBLISHED: 'published',
    };
    return statusMap[status] || 'drafting';
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading manuscript...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.colors.error,
            }}>
              <p style={{
                fontSize: '18px',
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchManuscript}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Main Content */}
        {!loading && !error && manuscript && (
          <>
            {/* Header Section */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '32px',
              gap: '20px',
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: '12px' }}>
                  {getStatusBadge(mapStatus(manuscript.status))}
                </div>
                <h1 style={{
                  fontSize: '40px',
                  fontWeight: '400',
                  marginBottom: '8px',
                  color: theme.colors.textPrimary,
                }}>
                  {manuscript.title}
                </h1>
            <div style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
              fontFamily: theme.fonts.monospace,
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
            }}>
              <span>{manuscript.primaryGenre}</span>
              {manuscript.subGenre && (
                <>
                  <span>•</span>
                  <span>{manuscript.subGenre}</span>
                </>
              )}
              <span>•</span>
              <span>{manuscript.wordCount.toLocaleString()} words</span>
            </div>
          </div>
          <Button onClick={() => window.location.href = `/writer/manuscripts/${manuscriptId}/edit`}>
            Edit Manuscript
          </Button>
        </div>

        {/* Quick Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Beta Readers
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.primary,
              fontFamily: theme.fonts.monospace,
            }}>
              {manuscript.betaReaderCount}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Applications
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.primary,
              fontFamily: theme.fonts.monospace,
            }}>
              {manuscript.applicationCount}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Last Updated
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
              marginTop: '8px',
            }}>
              {manuscript.lastUpdated}
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '32px',
          marginBottom: '32px',
        }}>
          {/* Left Column - Manuscript Details */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Synopsis */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Synopsis
              </h2>
              <p style={{
                fontSize: '16px',
                lineHeight: '1.8',
                color: theme.colors.textPrimary,
                whiteSpace: 'pre-wrap',
              }}>
                {manuscript.synopsis}
              </p>
            </Card>

            {/* First Pages */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                First Pages
              </h2>
              <div style={{
                fontSize: '15px',
                lineHeight: '1.8',
                color: theme.colors.textPrimary,
                whiteSpace: 'pre-wrap',
                fontFamily: theme.fonts.monospace,
                backgroundColor: theme.colors.backgroundAlt,
                padding: '20px',
                borderRadius: theme.borderRadius.medium,
                maxHeight: '400px',
                overflow: 'auto',
              }}>
                {manuscript.firstPages}
              </div>
            </Card>

            {/* Comparative Titles */}
            {manuscript.compTitles && manuscript.compTitles.length > 0 && (
              <Card>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  Comparative Titles
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                  {manuscript.compTitles.map((title: string, index: number) => (
                    <li
                      key={index}
                      style={{
                        fontSize: '16px',
                        color: theme.colors.textPrimary,
                        paddingLeft: '24px',
                        position: 'relative',
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        color: theme.colors.primary,
                      }}>
                        •
                      </span>
                      {title}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Content Warnings */}
            {manuscript.contentWarnings && (
              <Card style={{ backgroundColor: theme.colors.backgroundAlt }}>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  Content Warnings
                </h2>
                <p style={{
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: theme.colors.textSecondary,
                }}>
                  {manuscript.contentWarnings}
                </p>
              </Card>
            )}
          </div>

          {/* Right Column - Management */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Status Management */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Status Management
              </h3>
              <Select
                label="Manuscript Status"
                value={manuscript.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                options={statusOptions}
              />
              <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginTop: '12px',
                lineHeight: '1.5',
              }}>
                Update your manuscript's current stage in the publishing process
              </p>
            </Card>

            {/* Beta Reader Toggle */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Beta Reader Applications
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}>
                <span style={{
                  fontSize: '15px',
                  color: theme.colors.textPrimary,
                }}>
                  {manuscript.seekingBetaReaders ? 'Accepting applications' : 'Applications closed'}
                </span>
                <div
                  onClick={toggleBetaReaders}
                  style={{
                    width: '52px',
                    height: '28px',
                    backgroundColor: manuscript.seekingBetaReaders ? theme.colors.primary : theme.colors.border,
                    borderRadius: '14px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: theme.colors.white,
                    borderRadius: '12px',
                    position: 'absolute',
                    top: '2px',
                    left: manuscript.seekingBetaReaders ? '26px' : '2px',
                    transition: 'left 0.2s',
                  }} />
                </div>
              </div>
              <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                lineHeight: '1.5',
              }}>
                Toggle to control whether readers can apply to beta read this manuscript
              </p>
            </Card>

            {/* View Applications */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Beta Reader Management
              </h3>
              <Button
                fullWidth
                onClick={() => window.location.href = `/writer/manuscripts/${manuscriptId}/applications`}
              >
                View Applications ({manuscript.applicationCount})
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => window.location.href = `/writer/manuscripts/${manuscriptId}/readers`}
                style={{ marginTop: '12px' }}
              >
                Manage Active Readers ({manuscript.betaReaderCount})
              </Button>
            </Card>

            {/* Share & Export */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Share & Export
              </h3>
              <Button
                variant="outline"
                fullWidth
                onClick={handleShare}
              >
                Copy Share Link
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={handleExport}
                style={{ marginTop: '12px' }}
              >
                Export as PDF
              </Button>
            </Card>

            {/* Metadata */}
            <Card style={{ backgroundColor: theme.colors.backgroundAlt }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Metadata
              </h3>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                lineHeight: '1.6',
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: theme.colors.textPrimary }}>Created:</strong> {manuscript.createdAt}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: theme.colors.textPrimary }}>Last Updated:</strong> {manuscript.lastUpdated}
                </div>
                <div>
                  <strong style={{ color: theme.colors.textPrimary }}>ID:</strong> {manuscript.id}
                </div>
              </div>
            </Card>
          </div>
        </div>

            {/* Back Button */}
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/writer/manuscripts'}
            >
              ← Back to My Manuscripts
            </Button>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};
